import React, { useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useUserStore from "../../../stores/userStore";
import { useHeyGenStore } from "../../../stores/heygenStore";
import { HeyGenAvatar } from "../heygen-avatar/heygen-avatar";
import {
  dialogPaperStyles,
  roleplayFullscreenPaperStyles,
  roleplayPhoneLandscapePaperStyles,
  dialogTitleStyles,
  roleplayPhoneLandscapeTitleStyles,
  dialogTitleTextStyles,
  closeButtonStyles,
  roleplayPhoneLandscapeCloseButtonStyles,
  dialogContentStyles,
  roleplayFullscreenContentStyles,
  roleplayPhoneLandscapeContentStyles,
  contentBoxStyles,
  roleplayPhoneLandscapeQuery,
} from "./styles";

interface HeyGenAvatarDialogProps {
  open: boolean;
  onClose: () => void;
  onFallbackAudioOnly?: () => void | Promise<void>;
  avatarId?: string;
  voiceId?: string;
  auth?: {
    [key: string]: any;
  };
}

export const HeyGenAvatarDialog: React.FC<HeyGenAvatarDialogProps> = ({
  open,
  onClose,
  onFallbackAudioOnly,
  avatarId,
  voiceId,
  auth,
}) => {
  const { user } = useUserStore();
  const { windowTitle } = useHeyGenStore();
  const endSessionRef = useRef<
    ((options?: { close?: boolean }) => Promise<void>) | null
  >(null);

  const heygenAvatarId = avatarId || "default";
  const isRolePlay = Boolean(auth?.selling_dialogue);
  const isCoachMe = auth?.realtime_mode === "coach_me";
  const isPhoneLandscape = useMediaQuery(roleplayPhoneLandscapeQuery);
  const isRoleplayPhoneLandscape = isRolePlay && isPhoneLandscape;

  const dialogTitle =
    windowTitle ||
    (isCoachMe ? "Coaching Practice" : isRolePlay ? "Roleplay" : "");

  useEffect(() => {
    if (!open && endSessionRef.current) {
      // close:false — tear down media/socket without calling parent onClose
      // (which would wipe persona/mode during video→audio fallback).
      endSessionRef.current({ close: false }).catch(() => {});
    }
  }, [open]);

  const handleClose = async (event?: {}, reason?: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    if (endSessionRef.current) {
      try {
        await endSessionRef.current();
      } catch (error) {
        console.error("Error ending session:", error);
      }
    }
    onClose();
  };

  const handleSessionEnd = () => {
    onClose();
  };

  const handleError = (error: string) => {
    console.error("HeyGen Avatar error:", error);
  };

  const handleEndSessionReady = (
    endSession: (options?: { close?: boolean }) => Promise<void>,
  ) => {
    endSessionRef.current = endSession;
  };

  const paperSx = [
    isRolePlay ? roleplayFullscreenPaperStyles : dialogPaperStyles,
    isRoleplayPhoneLandscape ? roleplayPhoneLandscapePaperStyles : null,
  ] as SxProps<Theme>;

  const titleSx = [
    dialogTitleStyles,
    isRoleplayPhoneLandscape ? roleplayPhoneLandscapeTitleStyles : null,
  ] as SxProps<Theme>;

  const closeButtonSx = [
    closeButtonStyles,
    isRoleplayPhoneLandscape ? roleplayPhoneLandscapeCloseButtonStyles : null,
  ] as SxProps<Theme>;

  const contentSx = [
    dialogContentStyles,
    isRolePlay ? roleplayFullscreenContentStyles : null,
    isRoleplayPhoneLandscape ? roleplayPhoneLandscapeContentStyles : null,
  ] as SxProps<Theme>;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isRolePlay ? false : "md"}
      fullWidth={!isRolePlay}
      fullScreen={isRolePlay}
      disableEscapeKeyDown
      PaperProps={{
        sx: paperSx,
      }}
    >
      <DialogTitle sx={titleSx}>
        <Typography variant="h6" sx={dialogTitleTextStyles}>
          {dialogTitle}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={closeButtonSx}
          size={isRoleplayPhoneLandscape ? "small" : "medium"}
        >
          <CloseIcon fontSize={isRoleplayPhoneLandscape ? "small" : "medium"} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={contentSx}>
        {open && (
          <Box sx={contentBoxStyles}>
            <HeyGenAvatar
              avatarId={heygenAvatarId}
              voiceId={voiceId || "default"}
              onSessionEnd={handleSessionEnd}
              onError={handleError}
              auth={auth}
              user_id={user?.id}
              onEndSessionReady={handleEndSessionReady}
              onFallbackAudioOnly={onFallbackAudioOnly}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
